/**
 * TMS Dashboard — Local Transport Overview
 * General transport operations: deliveries, pickups, local distribution
 *
 * AI Agents: Jerry (Dispatch Optimizer), Helen (Document Validator & Biller),
 *            Grace (Customer Success), Bean (Finance & Driver Pay)
 *
 * Data: fetched from dashboard widget-data API + AI employees API
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useRenderMode } from '@/hooks/useRenderMode';
import { useDashboardData } from '@/hooks/useDashboardData';
import { useAiTeam, TMS_AI_AGENTS_SPEC, type AiEmployee } from '@/hooks/useAiTeam';
import { DataErrorBanner, type DataErrorDetail } from '@/components/ui/data-error-banner';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Truck, Container, RefreshCcw, AlertTriangle, Clock,
  CheckCircle2, TrendingUp, TrendingDown,
  Bot, ArrowRight, Calendar, DollarSign,
  Activity, Users, Package, Loader2,
} from 'lucide-react';
import { ComplianceDashboardCard } from '@/components/compliance';
import { api } from '@/lib/api';

// ── Config ────────────────────────────────────────────────────────────────────

const KPI_CONFIG = [
  { key: 'activeJobs', label: 'Active Jobs', icon: Truck, color: 'text-blue-600', slug: 'jobs', drillTo: '/tm/jobs?status=booked,confirmed,dispatched,in_transit' },
  { key: 'trips', label: 'Active Trips', icon: Container, color: 'text-orange-600', slug: 'job-trips', drillTo: '/tm/jobs?status=dispatched,in_transit' },
  { key: 'parties', label: 'Parties', icon: Users, color: 'text-green-600', slug: 'job-parties', drillTo: '/tm/jobs' },
  { key: 'ledger', label: 'Ledger Entries', icon: DollarSign, color: 'text-red-600', slug: 'ledger-entries', drillTo: '/tm/pricing' },
];

const statusConfig: Record<string, { label: string; color: string }> = {
  draft:       { label: 'Draft',      color: 'bg-gray-100 text-gray-600' },
  booked:      { label: 'Booked',     color: 'bg-blue-100 text-blue-700' },
  collecting:  { label: 'Collecting', color: 'bg-purple-100 text-purple-700' },
  at_port:     { label: 'At Port',    color: 'bg-orange-100 text-orange-700' },
  in_transit:  { label: 'In Transit', color: 'bg-yellow-100 text-yellow-700' },
  delivered:   { label: 'Delivered',  color: 'bg-green-100 text-green-700' },
  completed:   { label: 'Completed',  color: 'bg-green-100 text-green-800' },
};

const typeConfig: Record<string, string> = {
  'IMP+RET': 'bg-blue-50 text-blue-700 border-blue-200',
  'IMP':     'bg-cyan-50 text-cyan-700 border-cyan-200',
  'EXP':     'bg-indigo-50 text-indigo-700 border-indigo-200',
  'COL+EXP': 'bg-violet-50 text-violet-700 border-violet-200',
  'T/T':     'bg-slate-50 text-slate-700 border-slate-200',
};

const SERVICE_MODE_BADGES: Record<string, string> = {
  ROUND_TRIP:  '🔁',
  ONE_WAY:     '➡️',
  STREET_TURN: '🔄',
};

type CommercialQueueSummary = {
  ready_to_cost?: number;
  ready_to_invoice?: number;
  partial_paid?: number;
  settled?: number;
  [key: string]: number | undefined;
};

type CommercialQueueResponse = {
  summary: CommercialQueueSummary;
  items: Array<Record<string, unknown>>;
  generatedAt?: string;
};

// ── Component ────────────────────────────────────────────────────────────────

export default function TmsDashboard() {
  const isMobile = useRenderMode() === 'mobile';
  const [refreshing, setRefreshing] = useState(false);
  const [commercialSummary, setCommercialSummary] = useState<CommercialQueueSummary>({});

  // Fetch KPI counts + job status distribution + recent jobs from dashboard API
  const dashboard = useDashboardData({
    counts: KPI_CONFIG.map((k) => ({ key: k.key, slug: k.slug })),
    statusDistributions: [{ key: 'jobPipeline', slug: 'jobs' }],
    recentLists: [{ key: 'recentJobs', slug: 'jobs', limit: 8 }],
  });

  // Fetch AI team data from API, fallback to spec
  const { agents: apiAgents } = useAiTeam({ department: 'transportation' });
  const aiAgents: AiEmployee[] = apiAgents.length > 0 ? apiAgents : TMS_AI_AGENTS_SPEC;

  const kpiValues = KPI_CONFIG.map((k) => ({
    ...k,
    value: dashboard.counts[k.key] ?? 0,
  }));

  const jobPipeline = dashboard.distributions.jobPipeline ?? [];
  const recentJobs = dashboard.recentItems.recentJobs ?? [];

  const loadCommercialSummary = useCallback(async () => {
    try {
      const response = await api.get<CommercialQueueResponse>('/otc/tm/commercial-queue?limit=20&reflect=false');
      setCommercialSummary(response.summary ?? {});
    } catch {
      setCommercialSummary({});
    }
  }, []);

  useEffect(() => {
    void loadCommercialSummary();
  }, [loadCommercialSummary]);

  // Show error banner when dashboard returns no data
  const allCountsZero = !dashboard.loading && KPI_CONFIG.every((k) => (dashboard.counts[k.key] ?? 0) === 0);
  const dashError: DataErrorDetail | null = (allCountsZero && recentJobs.length === 0)
    ? { endpoint: '/dashboard/widget-data', message: dashboard.error || 'No data returned from dashboard API', status: 404, code: 'ENTITY_NOT_FOUND' }
    : null;

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    dashboard.refresh();
    void loadCommercialSummary();
    setTimeout(() => setRefreshing(false), 1200);
  }, [dashboard, loadCommercialSummary]);

  const commercialCards = useMemo(() => ([
    { key: 'ready_to_cost', label: 'Ready To Cost', value: commercialSummary.ready_to_cost ?? 0, color: 'text-sky-700' },
    { key: 'ready_to_invoice', label: 'Ready To Invoice', value: commercialSummary.ready_to_invoice ?? 0, color: 'text-blue-700' },
    { key: 'partial_paid', label: 'Partial Paid', value: commercialSummary.partial_paid ?? 0, color: 'text-orange-700' },
    { key: 'settled', label: 'Settled', value: commercialSummary.settled ?? 0, color: 'text-emerald-700' },
  ]), [commercialSummary]);

  if (isMobile) {
    return (
      <div className="space-y-3">
        {dashError && <DataErrorBanner error={dashError} entityLabel="TMS 仪表盘 (Dashboard)" onRetry={handleRefresh} compact />}
        {/* KPI grid */}
        <div className="grid grid-cols-2 gap-2">
          {kpiValues.map((kpi) => {
            const Icon = kpi.icon;
            return (
              <Card key={kpi.key} className="overflow-hidden">
                <CardContent className="p-3">
                  <Icon className={`h-4 w-4 mb-1.5 ${kpi.color}`} />
                  <div className="text-xl font-bold">{dashboard.loading ? '—' : kpi.value}</div>
                  <div className="text-[10px] text-muted-foreground">{kpi.label}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        {/* AI Agents */}
        <Card>
          <CardHeader className="pb-2 pt-3 px-3">
            <CardTitle className="text-xs flex items-center gap-1"><Bot className="h-3.5 w-3.5 text-violet-500" /> AI Agents</CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <div className="grid grid-cols-2 gap-1.5">
              {aiAgents.map((a) => (
                <div key={a.id} className="flex items-center gap-1.5 rounded-lg border px-2 py-1.5">
                  <div className={`h-1.5 w-1.5 rounded-full shrink-0 ${a.status === 'active' ? 'bg-green-500' : a.status === 'processing' ? 'bg-yellow-500 animate-pulse' : 'bg-gray-300'}`} />
                  <div className="min-w-0">
                    <div className="text-xs font-semibold leading-tight">{a.name}</div>
                    <div className="text-[10px] text-muted-foreground">{a.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        {/* Live jobs */}
        <Card>
          <CardHeader className="pb-2 pt-3 px-3">
            <CardTitle className="text-xs flex items-center gap-1"><Truck className="h-3.5 w-3.5" /> Recent Jobs</CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 space-y-2">
            {recentJobs.slice(0, 5).map((j, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[11px] font-semibold truncate">{String(j.jobCode ?? j.job_code ?? `JOB-${i + 1}`)}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{String(j.containerNo ?? j.container_no ?? '')} · {String(j.customerName ?? j.customer_name ?? '')}</div>
                </div>
                <Badge variant="outline" className="text-[9px] shrink-0">{String(j.status ?? '—')}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2 pt-3 px-3">
            <CardTitle className="text-xs flex items-center gap-1"><DollarSign className="h-3.5 w-3.5 text-primary" /> Commercial Queue</CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              {commercialCards.map((item) => (
                <div key={item.key} className="rounded-lg border px-2 py-2">
                  <div className="text-[10px] text-muted-foreground">{item.label}</div>
                  <div className={`text-lg font-semibold ${item.color}`}>{item.value}</div>
                </div>
              ))}
            </div>
            <Button variant="outline" className="h-9 w-full text-sm" asChild>
              <Link to="/tm/commercial-queue">Open Commercial Queue</Link>
            </Button>
          </CardContent>
        </Card>
        {/* Quick actions */}
        <div className="grid grid-cols-2 gap-2">
          <Button className="h-11 text-sm" asChild>
            <Link to="/tm/jobs/new"><Truck className="mr-2 h-4 w-4" /> New Job</Link>
          </Button>
          <Button variant="outline" className="h-11 text-sm" onClick={handleRefresh}>
            <RefreshCcw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">      {dashError && <DataErrorBanner error={dashError} entityLabel="TMS 仪表盘 (Dashboard)" onRetry={handleRefresh} />}
      {/* ── Top Actions ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Operations Overview</h2>
          <p className="text-sm text-muted-foreground">{new Date().toLocaleDateString('en-SG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} — Real time data from AI agents</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCcw className={`mr-1.5 h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/tm/commercial-queue">Commercial Queue</Link>
          </Button>
          <Button size="sm" asChild>
            <Link to="/tm/jobs/new">
              <Truck className="mr-1.5 h-3.5 w-3.5" />
              New Job
            </Link>
          </Button>
        </div>
      </div>

      {/* ── KPI Cards — clickable drill-down ────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpiValues.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <Link key={kpi.key} to={kpi.drillTo}>
              <Card className="hover:shadow-md transition-shadow cursor-pointer group">
                <CardContent className="pt-4 pb-4 px-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-muted-foreground">{kpi.label}</span>
                    <Icon className={`h-4 w-4 ${kpi.color}`} />
                  </div>
                  <div className="text-2xl font-bold">{dashboard.loading ? '—' : kpi.value}</div>
                  <div className="text-[10px] text-muted-foreground group-hover:text-primary transition-colors">
                    点击查看详情 →
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* ── Main Content Grid ────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* ── Recent Jobs (2/3 width) ──────────────────────────────────── */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Activity className="h-4 w-4 text-primary" />
              Recent Jobs
            </CardTitle>
            <Button variant="ghost" size="sm" asChild className="text-xs">
              <Link to="/tm/jobs">View All <ArrowRight className="ml-1 h-3 w-3" /></Link>
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/30">
                  <tr>
                    {['Job Code', 'Type', 'Container', 'Customer', 'Status', 'Created'].map((h) => (
                      <th key={h} className="text-left px-4 py-2 text-xs text-muted-foreground font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dashboard.loading ? (
                    <tr><td colSpan={6} className="text-center py-6 text-xs text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading...</td></tr>
                  ) : recentJobs.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-6 text-xs text-muted-foreground">No jobs found</td></tr>
                  ) : recentJobs.slice(0, 6).map((job, i) => {
                    const jobStatus = String(job.status ?? '');
                    const jobType = String(job.jobType ?? job.job_type ?? '');
                    return (
                      <tr key={i} className="border-b last:border-0 hover:bg-muted/20">
                        <td className="px-4 py-2.5">
                          <Link to={`/tm/jobs/${String(job.id ?? '')}`} className="font-mono text-xs font-medium text-primary hover:underline">
                            {String(job.jobCode ?? job.job_code ?? `J-${i + 1}`)}
                          </Link>
                        </td>
                        <td className="px-4 py-2.5">
                          {(() => {
                            const sm = String(job.serviceMode ?? job.service_mode ?? '');
                            const badge = SERVICE_MODE_BADGES[sm];
                            const smLabel: Record<string, string> = { ROUND_TRIP: 'Round Trip', ONE_WAY: 'One Way', STREET_TURN: 'Street Turn' };
                            if (badge) {
                              return (
                                <>
                                  <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold bg-blue-50 text-blue-700 border-blue-200">
                                    {badge} {smLabel[sm] ?? sm}
                                  </span>
                                  <div className="mt-0.5">
                                    <span className={`inline-flex rounded-md px-1.5 py-0.5 text-[9px] ${typeConfig[jobType] ?? 'bg-gray-50 text-gray-700'}`}>
                                      {jobType || '—'}
                                    </span>
                                  </div>
                                </>
                              );
                            }
                            return (
                              <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${typeConfig[jobType] ?? 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                                {jobType || '—'}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs">
                          {String(job.containerNo ?? job.container_no ?? '—')}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground truncate max-w-[120px]">{String(job.customerName ?? job.customer_name ?? '—')}</td>
                        <td className="px-4 py-2.5">
                          <Badge className={`text-[10px] px-1.5 ${statusConfig[jobStatus]?.color ?? 'bg-gray-100 text-gray-600'}`} variant="outline">
                            {statusConfig[jobStatus]?.label ?? (jobStatus || '—')}
                          </Badge>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground">{String(job.createdAt ?? job.created_at ?? '—')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* ── Right side column ─────────────────────────────────────────── */}
        <div className="space-y-4">

          {/* Job Status Distribution */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Calendar className="h-4 w-4 text-primary" />
                Job Pipeline
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-0">
              {jobPipeline.length === 0 && !dashboard.loading && (
                <div className="text-xs text-muted-foreground text-center py-4">No data available</div>
              )}
              {jobPipeline.map((g) => {
                const total = jobPipeline.reduce((s, x) => s + x.count, 0);
                const pct = total > 0 ? Math.round((g.count / total) * 100) : 0;
                const cfg = statusConfig[g.status];
                return (
                  <div key={g.status}>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="font-medium">{cfg?.label ?? g.status.replace(/_/g, ' ')}</span>
                      <span className="text-muted-foreground">{g.count} ({pct}%)</span>
                    </div>
                    <Progress value={pct} className="h-1.5" />
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-primary" />
                Commercial Queue
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-0">
              <div className="grid grid-cols-2 gap-2">
                {commercialCards.map((item) => (
                  <div key={item.key} className="rounded-lg border px-3 py-2">
                    <div className="text-[11px] text-muted-foreground">{item.label}</div>
                    <div className={`mt-1 text-xl font-semibold ${item.color}`}>{item.value}</div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="flex-1" asChild>
                  <Link to="/tm/commercial-queue">Open Queue</Link>
                </Button>
                <Button variant="outline" size="sm" className="flex-1" asChild>
                  <Link to="/tm/billing">Open Billing</Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Compliance Overview */}
          <ComplianceDashboardCard />
        </div>
      </div>

      {/* ── AI Agent Status ──────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Bot className="h-4 w-4 text-primary" />
            AI Workforce — Trucking Agents
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {aiAgents.map((agent) => (
              <div key={agent.id} className="rounded-lg border p-3 text-center hover:shadow-sm transition-shadow">
                <div className="flex justify-center mb-2">
                  <div className={`relative h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                    agent.status === 'active' ? 'bg-green-500' :
                    agent.status === 'processing' ? 'bg-amber-500' : 'bg-gray-400'
                  }`}>
                    {agent.name[0]}
                    <span className={`absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-background ${
                      agent.status === 'active' ? 'bg-green-400' :
                      agent.status === 'processing' ? 'bg-amber-400 animate-pulse' : 'bg-gray-300'
                    }`} />
                  </div>
                </div>
                <div className="font-semibold text-xs">{agent.name}</div>
                <div className="text-[10px] text-muted-foreground">{agent.role}</div>
                <div className="mt-1.5 text-[10px]">
                  <span className="font-medium">{agent.metrics?.processed ?? 0}</span>
                  <span className="text-muted-foreground"> done</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
